// Phase 8a — Property Management hub. 5 tabs: Overview / Tenant / Lease / Maintenance / Documents.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useFmt } from "@/lib/useFmt";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertOctagon,
  ArrowLeft,
  ArrowUpRight,
  Building2,
  CalendarCheck,
  CheckCircle2,
  Clock,
  FileSignature,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";

import TenantForm from "@/components/manage/TenantForm";
import LeaseForm from "@/components/manage/LeaseForm";
import MaintenanceForm from "@/components/manage/MaintenanceForm";
import ActivityFeed from "@/components/manage/ActivityFeed";
import DocumentUpload from "@/components/manage/DocumentUpload";
import InviteModal from "@/components/manage/InviteModal";

function Pill({ status }) {
  const map = {
    occupied: "bg-emerald-100 text-emerald-700",
    vacant: "bg-slate-100 text-slate-600",
    ending_soon: "bg-amber-100 text-amber-700",
    draft: "bg-slate-100 text-slate-600",
    active: "bg-emerald-100 text-emerald-700",
    expired: "bg-slate-100 text-slate-500",
    terminated: "bg-red-100 text-red-700",
    open: "bg-amber-100 text-amber-700",
    in_progress: "bg-indigo-100 text-indigo-700",
    resolved: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-slate-100 text-slate-500",
  };
  const label = (status || "").replace("_", " ");
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {label}
    </span>
  );
}

function Kpi({ label, value, hint, tone = "slate", testid }) {
  const tones = {
    slate: "border-slate-200 bg-white",
    indigo: "border-indigo-200 bg-indigo-50/50",
    emerald: "border-emerald-200 bg-emerald-50/50",
    amber: "border-amber-200 bg-amber-50/50",
    red: "border-red-200 bg-red-50/50",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border ${tones[tone]} p-4 shadow-sm`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-2 text-xl font-bold tracking-tight tabular-nums text-slate-900">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

function priorityTone(p) {
  return {
    emergency: "border-red-300 bg-red-50 text-red-800",
    high: "border-orange-300 bg-orange-50 text-orange-800",
    medium: "border-amber-300 bg-amber-50 text-amber-800",
    low: "border-slate-300 bg-slate-50 text-slate-700",
  }[p] || "border-slate-300 bg-slate-50 text-slate-700";
}

export default function PropertyHub() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fmt = useFmt();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const [tenantForm, setTenantForm] = useState({ open: false, tenant: null });
  const [leaseForm, setLeaseForm] = useState({ open: false, lease: null });
  const [maintForm, setMaintForm] = useState({ open: false, item: null });
  const [inviteFor, setInviteFor] = useState(null);  // Phase 8b — tenant portal invite

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ov }, { data: ts }, { data: ls }, { data: ms }, { data: ds }] = await Promise.all([
        api.get(`/manage/properties/${id}/overview`),
        api.get("/manage/tenants", { params: { property_id: id } }),
        api.get("/manage/leases", { params: { property_id: id } }),
        api.get("/manage/maintenance", { params: { property_id: id } }),
        api.get(`/manage/properties/${id}/documents`),
      ]);
      setOverview(ov);
      setTenants(ts);
      setLeases(ls);
      setMaintenance(ms);
      setDocuments(ds);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load property");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !overview) {
    return (
      <AppShell>
        <div className="flex h-64 items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </AppShell>
    );
  }

  const { property, current_lease, current_tenant, vacancy_status, lease_days_remaining, open_maintenance_count, monthly_rent_actual, monthly_rent_projected, rent_variance_pct } = overview;

  const activeLeases = leases.filter((l) => l.status !== "expired" && l.status !== "terminated");
  const pastLeases = leases.filter((l) => l.status === "expired" || l.status === "terminated");
  const openMaint = maintenance.filter((m) => m.status === "open");
  const inProgress = maintenance.filter((m) => m.status === "in_progress");
  const resolvedMaint = maintenance.filter((m) => m.status === "resolved");

  const activeTenant = tenants.find((t) => t.status === "active");
  const otherTenants = tenants.filter((t) => t.id !== activeTenant?.id);

  const resolveMaint = async (mid) => {
    try {
      await api.post(`/manage/maintenance/${mid}/resolve`);
      toast.success("Marked resolved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve");
    }
  };

  const deleteMaint = async (mid) => {
    if (!window.confirm("Delete this maintenance item?")) return;
    try {
      await api.delete(`/manage/maintenance/${mid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const deleteTenant = async (tid) => {
    if (!window.confirm("Delete this tenant? Will soft-delete if lease history exists.")) return;
    try {
      await api.delete(`/manage/tenants/${tid}`);
      toast.success("Done");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const activateLease = async (lid) => {
    try {
      await api.post(`/manage/leases/${lid}/activate`);
      toast.success("Lease activated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const terminateLease = async (lid) => {
    const reason = window.prompt("Reason for termination?");
    if (!reason) return;
    try {
      await api.post(`/manage/leases/${lid}/terminate`, { reason });
      toast.success("Lease terminated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const uploadLeaseDoc = async (lid, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/manage/leases/${lid}/document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Lease document uploaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to upload");
    }
  };

  const generateSchedule = async (lid) => {
    try {
      const { data } = await api.post(`/manage/leases/${lid}/payments/generate-schedule`);
      toast.success(`Created ${data.count} payment record${data.count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate schedule");
    }
  };

  return (
    <AppShell>
      <div className="space-y-6 fade-in" data-testid="property-hub">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/manage")}
              className="-ml-2 text-slate-500 hover:text-slate-900"
              data-testid="hub-back"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> All properties
            </Button>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Building2 className="h-5 w-5 text-indigo-600" />
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="hub-address">
                {property.address}
              </h1>
              <Pill status={vacancy_status} />
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {property.city}, {property.state || property.country}
            </p>
          </div>
          <Link
            to={`/deals/${(overview.property?.deal_id) || ""}`}
            onClick={(e) => {
              e.preventDefault();
              // We don't have direct deal_id on the property — go via the deal list endpoint
              api.get("/deals", { params: { property_id: property.id } }).then(({ data }) => {
                const deal = (data || []).find((d) => d.property_id === property.id);
                if (deal) navigate(`/deals/${deal.id}`);
                else toast.error("No analysis on file for this property");
              });
            }}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            data-testid="hub-open-analysis"
          >
            Open analysis <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>

        {/* KPI strip */}
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="hub-kpis">
          <Kpi label="Status" value={vacancy_status === "occupied" ? "Occupied" : vacancy_status === "ending_soon" ? "Ending soon" : "Vacant"} tone={vacancy_status === "vacant" ? "slate" : vacancy_status === "ending_soon" ? "amber" : "emerald"} testid="hub-kpi-status" />
          <Kpi label="Current tenant" value={current_tenant?.name || "—"} tone={current_tenant ? "indigo" : "slate"} testid="hub-kpi-tenant" />
          <Kpi label="Monthly rent" value={monthly_rent_actual ? fmt.money(monthly_rent_actual, { cents: false }) : "—"} hint={rent_variance_pct !== null && Math.abs(rent_variance_pct) >= 5 ? `${rent_variance_pct > 0 ? "+" : ""}${rent_variance_pct.toFixed(0)}% vs model` : null} tone="slate" testid="hub-kpi-rent" />
          <Kpi label="Lease days left" value={lease_days_remaining === null || lease_days_remaining === undefined ? "—" : lease_days_remaining} tone={lease_days_remaining !== null && lease_days_remaining <= 30 ? "amber" : "slate"} testid="hub-kpi-days" />
          <Kpi label="Open maintenance" value={open_maintenance_count} tone={open_maintenance_count > 0 ? "amber" : "slate"} testid="hub-kpi-maint" />
        </section>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100" data-testid="hub-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="tenant" data-testid="tab-tenant">Tenant</TabsTrigger>
            <TabsTrigger value="lease" data-testid="tab-lease">Lease</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-5 space-y-5" data-testid="hub-tab-overview">
            <div className="grid gap-5 lg:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
                <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Activity</h3>
                <div className="mt-4">
                  <ActivityFeed events={overview.recent_activity} />
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Quick actions</h3>
                <div className="mt-4 grid gap-2">
                  <Button onClick={() => setTenantForm({ open: true, tenant: null })} className="justify-start bg-indigo-600 text-white hover:bg-indigo-700" data-testid="quick-add-tenant">
                    <Plus className="mr-2 h-4 w-4" /> Add tenant
                  </Button>
                  <Button onClick={() => setLeaseForm({ open: true, lease: null })} variant="outline" className="justify-start" data-testid="quick-add-lease">
                    <FileSignature className="mr-2 h-4 w-4" /> Start a lease
                  </Button>
                  <Button onClick={() => setMaintForm({ open: true, item: null })} variant="outline" className="justify-start" data-testid="quick-add-maint">
                    <Wrench className="mr-2 h-4 w-4" /> Log maintenance
                  </Button>
                  <Button onClick={() => setActiveTab("documents")} variant="outline" className="justify-start" data-testid="quick-upload-doc">
                    <FileSignature className="mr-2 h-4 w-4" /> Upload document
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Tenant */}
          <TabsContent value="tenant" className="mt-5 space-y-5" data-testid="hub-tab-tenant">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Current tenant</h3>
              <Button onClick={() => setTenantForm({ open: true, tenant: null })} className="bg-indigo-600 text-white hover:bg-indigo-700" size="sm" data-testid="tenant-add-button">
                <Plus className="mr-2 h-3 w-3" /> Add tenant
              </Button>
            </div>

            {activeTenant ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="tenant-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold text-slate-900">{activeTenant.name}</p>
                    <p className="text-sm text-slate-500">{activeTenant.email} · {activeTenant.phone}</p>
                  </div>
                  <Pill status="active" />
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                  <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Employer</dt><dd className="text-slate-900">{activeTenant.employer || "—"}</dd></div>
                  <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Income</dt><dd className="text-slate-900">{activeTenant.monthly_income ? fmt.money(activeTenant.monthly_income, { cents: false }) : "—"}</dd></div>
                  <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Employment</dt><dd className="text-slate-900">{activeTenant.employment_type || "—"}</dd></div>
                </dl>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => setTenantForm({ open: true, tenant: activeTenant })} data-testid="tenant-edit-button">
                    <Pencil className="mr-2 h-3 w-3" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInviteFor(activeTenant)}
                    className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    data-testid={`tenant-invite-button-${activeTenant.id}`}
                  >
                    {activeTenant.user_id_portal ? "Portal active" : "Send portal invite"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => deleteTenant(activeTenant.id)} className="border-red-200 text-red-700 hover:bg-red-50">
                    <Trash2 className="mr-2 h-3 w-3" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-8 text-center" data-testid="tenant-empty">
                <p className="text-sm text-slate-500">No active tenant on this property.</p>
                <Button onClick={() => setTenantForm({ open: true, tenant: null })} className="mt-3 bg-indigo-600 text-white hover:bg-indigo-700">
                  <Plus className="mr-2 h-3 w-3" /> Add tenant
                </Button>
              </div>
            )}

            {otherTenants.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Tenant history</h4>
                <ul className="space-y-2">
                  {otherTenants.map((t) => (
                    <li key={t.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3" data-testid={`tenant-row-${t.id}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{t.name}</p>
                        <p className="text-xs text-slate-500">{t.email} · {t.phone}</p>
                      </div>
                      <Pill status={t.status} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          {/* Lease */}
          <TabsContent value="lease" className="mt-5 space-y-5" data-testid="hub-tab-lease">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Active lease</h3>
              <Button onClick={() => setLeaseForm({ open: true, lease: null })} className="bg-indigo-600 text-white hover:bg-indigo-700" size="sm" disabled={tenants.length === 0} data-testid="lease-add-button">
                <Plus className="mr-2 h-3 w-3" /> Start lease
              </Button>
            </div>

            {activeLeases.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-8 text-center" data-testid="lease-empty">
                <p className="text-sm text-slate-500">No leases yet on this property.</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {activeLeases.map((l) => {
                  const tenant = tenants.find((t) => t.id === l.tenant_id);
                  return (
                    <li key={l.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`lease-card-${l.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-base font-semibold text-slate-900">
                            {l.start_date} – {l.end_date} <span className="ml-2 text-xs font-normal text-slate-500">{l.term_months}mo</span>
                          </p>
                          <p className="mt-0.5 text-sm text-slate-500">Tenant: {tenant?.name || "—"}</p>
                        </div>
                        <Pill status={l.status} />
                      </div>
                      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                        <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Rent</dt><dd className="font-medium text-slate-900">{l.monthly_rent} {l.currency}/mo</dd></div>
                        <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Security</dt><dd className="text-slate-700">{l.security_deposit}</dd></div>
                        <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Pets</dt><dd className="text-slate-700">{l.pets_allowed ? "Yes" : "No"}</dd></div>
                        <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">Utilities incl.</dt><dd className="text-slate-700">{l.utilities_included?.length ? l.utilities_included.join(", ") : "None"}</dd></div>
                      </dl>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => setLeaseForm({ open: true, lease: l })} data-testid={`lease-edit-${l.id}`}>
                          <Pencil className="mr-2 h-3 w-3" /> Edit
                        </Button>
                        {l.status === "draft" && (
                          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => activateLease(l.id)} data-testid={`lease-activate-${l.id}`}>
                            <CheckCircle2 className="mr-2 h-3 w-3" /> Activate
                          </Button>
                        )}
                        {(l.status === "active" || l.status === "ending_soon" || l.status === "draft") && (
                          <Button variant="outline" size="sm" onClick={() => terminateLease(l.id)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`lease-terminate-${l.id}`}>
                            Terminate
                          </Button>
                        )}
                        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid={`lease-doc-${l.id}`}>
                          <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadLeaseDoc(l.id, e.target.files?.[0])} />
                          {l.document_key ? "Replace document" : "Upload signed PDF"}
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateSchedule(l.id)}
                          className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                          data-testid={`lease-gen-schedule-${l.id}`}
                          title="Pre-populate 12 months of rent payment records"
                        >
                          Generate 12-mo payment schedule
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {pastLeases.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Lease history</h4>
                <ul className="space-y-2">
                  {pastLeases.map((l) => (
                    <li key={l.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-sm">
                      <span>{l.start_date} – {l.end_date} · {l.monthly_rent} {l.currency}</span>
                      <Pill status={l.status} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          {/* Maintenance */}
          <TabsContent value="maintenance" className="mt-5 space-y-5" data-testid="hub-tab-maintenance">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Work orders</h3>
              <Button onClick={() => setMaintForm({ open: true, item: null })} className="bg-indigo-600 text-white hover:bg-indigo-700" size="sm" data-testid="maint-add-button">
                <Plus className="mr-2 h-3 w-3" /> New maintenance
              </Button>
            </div>

            <div className="grid gap-4 lg:grid-cols-3" data-testid="maint-kanban">
              {[
                { key: "open", title: "Open", rows: openMaint },
                { key: "in_progress", title: "In progress", rows: inProgress },
                { key: "resolved", title: "Resolved", rows: resolvedMaint },
              ].map((col) => (
                <div key={col.key} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4">
                  <p className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {col.title} <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-slate-700">{col.rows.length}</span>
                  </p>
                  <div className="space-y-2.5">
                    {col.rows.length === 0 && <p className="text-xs text-slate-400">Empty.</p>}
                    {col.rows.map((m) => (
                      <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`maint-card-${m.id}`}>
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className={`rounded-full border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider ${priorityTone(m.priority)}`}>
                            {m.priority}
                          </span>
                          <div className="flex items-center gap-1">
                            {m.tenant_reported && (
                              <span
                                data-testid={`maint-tenant-badge-${m.id}`}
                                className="rounded-full bg-emerald-100 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-emerald-700"
                                title="Tenant-reported"
                              >
                                Tenant
                              </span>
                            )}
                            <span className="text-[10px] text-slate-400">{m.category}</span>
                          </div>
                        </div>
                        <p className="text-sm font-semibold text-slate-900">{m.title}</p>
                        {m.description && <p className="mt-1 line-clamp-2 text-xs text-slate-600">{m.description}</p>}
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-[11px] text-slate-500">
                            {m.vendor_name ? `${m.vendor_name}` : "No vendor"} {m.actual_cost ? `· ${fmt.money(m.actual_cost, { cents: false })}` : m.estimated_cost ? `· est ${fmt.money(m.estimated_cost, { cents: false })}` : ""}
                          </span>
                        </div>
                        <div className="mt-2 flex gap-1.5">
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setMaintForm({ open: true, item: m })} data-testid={`maint-edit-${m.id}`}>
                            Edit
                          </Button>
                          {m.status !== "resolved" && (
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-emerald-700" onClick={() => resolveMaint(m.id)} data-testid={`maint-resolve-${m.id}`}>
                              Resolve
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-red-600" onClick={() => deleteMaint(m.id)} data-testid={`maint-delete-${m.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        {m.photos?.length > 0 && (
                          <p className="mt-1 text-[10px] text-slate-400">{m.photos.length} photo{m.photos.length !== 1 ? "s" : ""}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents" className="mt-5 space-y-5" data-testid="hub-tab-documents">
            <DocumentUpload propertyId={id} documents={documents} onChanged={load} />
          </TabsContent>
        </Tabs>
      </div>

      <TenantForm
        open={tenantForm.open}
        onOpenChange={(o) => setTenantForm({ open: o, tenant: o ? tenantForm.tenant : null })}
        propertyId={id}
        tenant={tenantForm.tenant}
        onSaved={load}
      />
      <LeaseForm
        open={leaseForm.open}
        onOpenChange={(o) => setLeaseForm({ open: o, lease: o ? leaseForm.lease : null })}
        propertyId={id}
        tenants={tenants.filter((t) => t.status !== "past" && t.status !== "rejected")}
        lease={leaseForm.lease}
        defaultCurrency={current_lease?.currency || "USD"}
        onSaved={load}
      />
      <MaintenanceForm
        open={maintForm.open}
        onOpenChange={(o) => setMaintForm({ open: o, item: o ? maintForm.item : null })}
        propertyId={id}
        item={maintForm.item}
        leaseId={current_lease?.id}
        onSaved={load}
      />
      <InviteModal
        open={!!inviteFor}
        onOpenChange={(o) => setInviteFor(o ? inviteFor : null)}
        tenant={inviteFor}
        onUpdated={load}
      />
    </AppShell>
  );
}
